'use client';

/**
 * useIsDesktop — shared JS matchMedia check for the Tailwind `sm` breakpoint
 * (640px). Extracted from voice/call-screen.tsx (design turn 11a) so a
 * second consumer (voice/pinned-card.tsx, design turn 14) can branch on the
 * SAME boundary via JS rather than mounting both a mobile and a desktop
 * subtree behind responsive classes — two `hidden sm:flex` / `sm:hidden`
 * subtrees would both still mount their contents (gesture handlers, Sheet
 * state, live rAF loops in call-orb's case), which is exactly what
 * CallScreen's original doc comment called out. Any future call site that
 * needs "exactly one branch mounts" behavior at this boundary should reuse
 * this hook rather than re-deriving its own matchMedia effect.
 */

import { useEffect, useState } from 'react';

const DESKTOP_MEDIA_QUERY = '(min-width: 640px)';

export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(DESKTOP_MEDIA_QUERY).matches
      : false
  );

  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_MEDIA_QUERY);
    setIsDesktop(mql.matches);
    const handleChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  return isDesktop;
}
